import { useCallback, useState } from 'react'

import { useMentionData } from '../../context/MentionContext'
import {
  isInterimExpanded as phaseIsInterimExpanded,
  isResolved as phaseIsResolved,
  selectCardPhase,
} from '../../../hooks/engagement/selectCardPhase'

type UseMentionStateProps = {
  expanded?: boolean
  onLocalResolutionChange?: (mentionId: string, isResolved: boolean) => void
  isLocallyResolved?: boolean
}

type UseMentionStateReturn = {
  actionsLoading: boolean
  isHovered: boolean
  isLocallyDismissed: boolean
  isLocallyReplied: boolean
  isDismissed: boolean
  isInterimExpanded: boolean
  isResolved: boolean
  handleActionsLoadingChange: (loading: boolean) => void
  handleMouseEnter: () => void
  handleMouseLeave: () => void
  handleDismissed: () => void
  handleUndismissed: () => void
  handleReplied: () => void
}

/**
 * Mirrors `useCommentState` (trimmed). Owns the card's local UI state —
 * hover, actions-loading — and relays dismiss/undo intent to the list,
 * which owns the resolution set (see the shared engagement coordination store).
 *
 * The actual mutation fires in `MentionActions` (mirror of `CommentActions`);
 * the card flips to dismissed only once the mutation succeeds and the list
 * marks it resolved (pessimistic — no card-local rollback). A card-local
 * `isLocallyReplied` flag keeps a just-published reply distinct from a
 * dismiss, mirroring `useCommentState`'s `replyStatus === 'published'`.
 */
export const useMentionState = ({
  expanded = false,
  onLocalResolutionChange,
  isLocallyResolved = false,
}: UseMentionStateProps = {}): UseMentionStateReturn => {
  const mention = useMentionData()
  const [actionsLoading, setActionsLoading] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  // A locally-published reply and a local dismiss both land in the list's
  // resolution set, so `isLocallyResolved` can't tell them apart. This
  // card-local flag records which one resolved it, mirroring
  // `useCommentState`'s `replyStatus === 'published'` distinction — only
  // dismissed cards get the reopen affordance, replied ones don't.
  const [isLocallyReplied, setIsLocallyReplied] = useState(false)

  const isLocallyDismissed = isLocallyResolved && !isLocallyReplied
  // Dismissed gets the reopen ("Undo dismiss") affordance, so it's kept as a
  // separate flag — computed locally (legacy formula) rather than from the
  // selector. The selector resolves `via` from server status first, which would
  // report a server-`replied` + locally-dismissed card as a reply; keeping this
  // local preserves the exact legacy value. Routing it through the selector is
  // deferred to the intentional semantics unification with the action reducer.
  const isDismissed = mention.status === 'dismissed' || isLocallyDismissed

  // One pure selector owns the resolution derivation shared with
  // `useCommentState` (see `selectCardPhase`). A resolved card (dismissed or
  // replied) reads as "done" so the list greys it out — until it's expanded,
  // when it returns to full colour and chrome.
  const phase = selectCardPhase({
    status: mention.status,
    expanded,
    isLocallyResolved,
    isLocallyReplied,
  })
  const isInterimExpanded = phaseIsInterimExpanded(phase)
  const isResolved = phaseIsResolved(phase)

  const handleActionsLoadingChange = useCallback((loading: boolean) => {
    setActionsLoading(loading)
  }, [])

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false)
  }, [])

  // Notify the list that this mention was dismissed / undismissed. The
  // list owns the resolution set; `MentionActions` only calls these after
  // its mutation succeeds.
  const handleDismissed = useCallback(() => {
    onLocalResolutionChange?.(mention.id, true)
  }, [mention.id, onLocalResolutionChange])

  const handleUndismissed = useCallback(() => {
    setIsLocallyReplied(false)
    onLocalResolutionChange?.(mention.id, false)
  }, [mention.id, onLocalResolutionChange])

  // A published reply resolves the card just like dismiss does — grey it out
  // and collapse it via the list's resolution set. The reply form calls this
  // on success (see `useMentionReplyForm`). The local flag keeps it distinct
  // from a dismiss so the card doesn't offer "Undo dismiss" after a reply.
  const handleReplied = useCallback(() => {
    setIsLocallyReplied(true)
    onLocalResolutionChange?.(mention.id, true)
  }, [mention.id, onLocalResolutionChange])

  return {
    actionsLoading,
    isHovered,
    isLocallyDismissed,
    isLocallyReplied,
    isDismissed,
    isInterimExpanded,
    isResolved,
    handleActionsLoadingChange,
    handleMouseEnter,
    handleMouseLeave,
    handleDismissed,
    handleUndismissed,
    handleReplied,
  }
}
